"use client";

/**
 * A sticky side rail that works at any height.
 *
 * A rail shorter than the window sticks under the header as before. A
 * taller one used to stick by its top, so its lower part stayed below
 * the window until the main column ran out, and only then could it be
 * scrolled to (owner, 2026-10-27, the residence page on a computer).
 * Now a tall rail scrolls with the page until its bottom reaches the
 * window's bottom and sticks there, so every part of it is reachable
 * while the main column scrolls.
 *
 * Returns the ref for the rail and the `top` to put in its style; the
 * rail keeps its own `sticky` class (on the breakpoints where it is a
 * rail at all). The first render uses `offset`, the server's value.
 */
import { useEffect, useRef, useState } from "react";

export function useStickyRail<T extends HTMLElement>(offset = 96, gap = 24) {
  const ref = useRef<T>(null);
  const [top, setTop] = useState(offset);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const h = el.offsetHeight;
      const room = window.innerHeight - offset - gap;
      setTop(h > room ? Math.round(window.innerHeight - h - gap) : offset);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [offset, gap]);
  return { ref, top };
}
